import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/client';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { FileText, Upload, Trash2, Download, Loader2, Search, Folder } from 'lucide-react';

const ALL = '__all__';
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtSize = (b) => b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;

export default function DocumentsPage() {
  const { get, del } = useApi();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('content.create');
  const [docs, setDocs] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [batchFilter, setBatchFilter] = useState(ALL);

  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [tags, setTags] = useState('');
  const [visibility, setVisibility] = useState('org');
  const [uploadBatch, setUploadBatch] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => { get('/trainees/batches').then(r => setBatches(r.batches || [])).catch(() => {}); }, [get]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (batchFilter !== ALL) params.set('batch_id', batchFilter);
      const r = await get(`/documents?${params}`);
      setDocs(r.documents || []);
    } catch { toast.error('Failed to load documents'); }
    finally { setLoading(false); }
  }, [get, search, batchFilter]);
  useEffect(() => { load(); }, [load]);

  const doUpload = async () => {
    if (!file) { toast.error('Choose a file'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', name || file.name);
      fd.append('tags', tags);
      fd.append('visibility', visibility);
      if (visibility === 'batch' && uploadBatch) fd.append('batch_id', uploadBatch);
      await api.post('/documents', fd);
      toast.success('Document uploaded');
      setOpen(false); setFile(null); setName(''); setTags(''); setVisibility('org'); setUploadBatch('');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    try { await del(`/documents/${d.document_id}`); toast.success('Deleted'); await load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="documents-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center"><Folder className="w-5 h-5 text-amber-600" /></div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Document Repository</h1>
            <p className="text-sm text-muted-foreground">Upload and share training documents across the org or per batch.</p>
          </div>
        </div>
        <Button onClick={() => setOpen(true)} disabled={!canWrite} data-testid="doc-upload-btn"><Upload className="w-4 h-4 mr-2" />Upload</Button>
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search documents…" className="pl-9" data-testid="doc-search" />
        </div>
        <Select value={batchFilter} onValueChange={setBatchFilter}>
          <SelectTrigger className="w-56" data-testid="doc-batch-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All documents</SelectItem>
            {batches.map(b => <SelectItem key={b.batch_id} value={b.batch_id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : docs.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="doc-empty">No documents yet.</Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {docs.map(d => (
            <Card key={d.document_id} className="p-4 space-y-3" data-testid={`doc-card-${d.document_id}`}>
              <div className="flex items-start gap-3">
                <FileText className="w-8 h-8 text-amber-600 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{d.name}</div>
                  <div className="text-xs text-muted-foreground">{fmtSize(d.size)} · {(d.uploaded_at || '').slice(0, 10)}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {d.visibility === 'batch' && <Badge variant="secondary" className="bg-warm-200 text-warm-800">Batch</Badge>}
                {(d.tags || []).map(t => <Badge key={t} variant="secondary">{t}</Badge>)}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" asChild data-testid={`doc-download-${d.document_id}`}>
                  <a href={`${BACKEND}${d.file_url}`} target="_blank" rel="noreferrer"><Download className="w-3.5 h-3.5 mr-1" />Open</a>
                </Button>
                {canWrite && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(d)} data-testid={`doc-delete-${d.document_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="doc-upload-dialog">
          <DialogHeader><DialogTitle>Upload document</DialogTitle><DialogDescription className="sr-only">Upload a file and set its name, tags, and visibility.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>File</Label>
              <Input type="file" onChange={(e) => { setFile(e.target.files?.[0] || null); if (e.target.files?.[0] && !name) setName(e.target.files[0].name); }} data-testid="doc-file-input" /></div>
            <div className="space-y-1.5"><Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Document name" data-testid="doc-name-input" /></div>
            <div className="space-y-1.5"><Label>Tags (comma-separated)</Label>
              <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="policy, onboarding" /></div>
            <div className="flex gap-3">
              <div className="space-y-1.5 flex-1"><Label>Visibility</Label>
                <Select value={visibility} onValueChange={setVisibility}>
                  <SelectTrigger data-testid="doc-visibility"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="org">Whole organization</SelectItem>
                    <SelectItem value="batch">Specific batch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {visibility === 'batch' && (
                <div className="space-y-1.5 flex-1"><Label>Batch</Label>
                  <Select value={uploadBatch} onValueChange={setUploadBatch}>
                    <SelectTrigger data-testid="doc-upload-batch"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{batches.map(b => <SelectItem key={b.batch_id} value={b.batch_id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={doUpload} disabled={uploading} data-testid="doc-upload-confirm">{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upload'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
